import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Save, Loader2, ArrowLeft, Search, RotateCcw, Package } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReturnsOrderCreate = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const [customers, setCustomers] = useState([]);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [distChannels, setDistChannels] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [selectedSO, setSelectedSO] = useState(null);
  
  const [header, setHeader] = useState({
    customer_id: '',
    reference_so_number: '',
    sales_organization: '',
    distribution_channel: '',
    return_date: new Date().toISOString().split('T')[0],
    return_reason: '',
    return_note: ''
  });
  
  const [items, setItems] = useState([]);

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    setIsLoading(true);
    try {
      const [custRes, orgRes, dcRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/distribution-channels`).catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data || []);
      setSalesOrgs(orgRes.data || []);
      setDistChannels(dcRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSalesOrders = async (customerId) => {
    if (!customerId) return;
    setIsSearching(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/otc/sales-orders?customer_id=${customerId}&status=DELIVERED`);
      // Also include COMPLETED and PARTIALLY_DELIVERED
      const response2 = await axios.get(`${BACKEND_URL}/api/otc/sales-orders?customer_id=${customerId}&status=COMPLETED`);
      const response3 = await axios.get(`${BACKEND_URL}/api/otc/sales-orders?customer_id=${customerId}&status=PARTIALLY_DELIVERED`);
      const allOrders = [...(response.data || []), ...(response2.data || []), ...(response3.data || [])];
      // Remove duplicates and filter only those with delivered items
      const uniqueOrders = Array.from(new Map(allOrders.map(o => [o.sales_order_number, o])).values());
      setSalesOrders(uniqueOrders.filter(o => o.items?.some(i => i.delivered_quantity > 0)));
    } catch (error) {
      console.error('Error loading sales orders:', error);
      toast.error('Failed to load delivered sales orders');
    } finally {
      setIsSearching(false);
    }
  };

  const handleCustomerChange = (customerId) => {
    setHeader({
      ...header,
      customer_id: customerId,
      reference_so_number: '',
      sales_organization: '',
      distribution_channel: ''
    });
    setSelectedSO(null);
    setItems([]);
    loadSalesOrders(customerId);
  };

  const handleSOSelect = (soNumber) => {
    const so = salesOrders.find(o => o.sales_order_number === soNumber);
    if (so) {
      setSelectedSO(so);
      setHeader({
        ...header,
        reference_so_number: soNumber,
        sales_organization: so.sales_organization,
        distribution_channel: so.distribution_channel
      });
      
      // Initialize items from delivered SO items
      const returnItems = so.items
        .filter(item => item.delivered_quantity > 0)
        .map((item, idx) => ({
          item_number: (idx + 1) * 10,
          reference_so_number: soNumber,
          reference_so_item: item.item_number,
          material_id: item.material_id,
          item_description: item.item_description,
          original_quantity: item.delivered_quantity,
          return_quantity: 0, // User must enter
          unit_of_measure: item.unit_of_measure,
          return_reason: '',
          net_price: item.net_price,
          selected: false
        }));
      setItems(returnItems);
    }
  };

  const handleItemToggle = (index, selected) => {
    const newItems = [...items];
    newItems[index].selected = selected;
    if (!selected) {
      newItems[index].return_quantity = 0;
      newItems[index].return_reason = '';
    }
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-select if quantity entered
    if (field === 'return_quantity' && value > 0) {
      newItems[index].selected = true;
    }
    
    setItems(newItems);
  };

  const validateForm = () => {
    if (!header.customer_id) {
      toast.error('Customer is required');
      return false;
    }
    if (!header.reference_so_number) {
      toast.error('Sales Order reference is required');
      return false;
    }
    if (!header.return_date) {
      toast.error('Return date is required');
      return false;
    }
    if (!header.return_reason) {
      toast.error('Return reason is required');
      return false;
    }
    
    const selectedItems = items.filter(i => i.selected && i.return_quantity > 0);
    if (selectedItems.length === 0) {
      toast.error('Select at least one item with return quantity');
      return false;
    }
    
    for (const item of selectedItems) {
      if (item.return_quantity > item.original_quantity) {
        toast.error(`Return quantity cannot exceed delivered quantity for item ${item.item_number}`);
        return false;
      }
      if (!item.return_reason) {
        toast.error(`Return reason is required for item ${item.item_number}`);
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSaving(true);
    try {
      const selectedItems = items
        .filter(i => i.selected && i.return_quantity > 0)
        .map(i => ({
          item_number: i.item_number,
          reference_so_number: i.reference_so_number,
          reference_so_item: i.reference_so_item,
          material_id: i.material_id,
          item_description: i.item_description,
          return_quantity: parseFloat(i.return_quantity),
          unit_of_measure: i.unit_of_measure,
          return_reason: i.return_reason,
          net_price: i.net_price,
          net_value: i.net_price * parseFloat(i.return_quantity)
        }));
      
      const payload = {
        customer_id: header.customer_id,
        reference_so_number: header.reference_so_number,
        sales_organization: header.sales_organization,
        distribution_channel: header.distribution_channel,
        return_date: header.return_date,
        return_reason: header.return_reason,
        return_note: header.return_note || null,
        items: selectedItems
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/otc/returns`, payload);
      toast.success(`Returns Order '${response.data.returns_number}' created successfully!`);
      navigate(`/otc/returns/${response.data.returns_number}`);
    } catch (error) {
      console.error('Error creating returns order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create returns order');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="returns-order-create">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/returns')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Returns Order</h1>
              <p className="text-sm text-gray-600">Process customer returns from delivered sales orders</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving} data-testid="save-returns-btn">
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Create Returns Order</>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Customer & SO Selection */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <RotateCcw className="w-5 h-5" /> Reference Data
            </h3>
            <div className="space-y-4">
              <div>
                <Label>Customer *</Label>
                <Select value={header.customer_id || '__SELECT__'} onValueChange={(v) => handleCustomerChange(v === '__SELECT__' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Select Customer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__SELECT__">Select Customer</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.customer_id} value={c.customer_id}>
                        {c.customer_id} - {c.customer_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Original Sales Order *</Label>
                {isSearching ? (
                  <div className="flex items-center gap-2 p-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading delivered orders...
                  </div>
                ) : (
                  <Select 
                    value={header.reference_so_number || '__SELECT__'} 
                    onValueChange={(v) => handleSOSelect(v === '__SELECT__' ? '' : v)}
                    disabled={!header.customer_id}
                  >
                    <SelectTrigger><SelectValue placeholder="Select Sales Order" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__SELECT__">Select Sales Order</SelectItem>
                      {salesOrders.map(so => (
                        <SelectItem key={so.sales_order_number} value={so.sales_order_number}>
                          {so.sales_order_number} - {so.document_date} (${so.gross_value?.toFixed(2) || '0.00'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {header.customer_id && salesOrders.length === 0 && !isSearching && (
                  <p className="text-sm text-orange-600 mt-1">No delivered sales orders found for this customer</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sales Organization</Label>
                  <Input value={header.sales_organization} disabled className="bg-gray-50" />
                </div>
                <div>
                  <Label>Distribution Channel</Label>
                  <Input value={header.distribution_channel} disabled className="bg-gray-50" />
                </div>
              </div>
            </div>
          </Card>

          {/* Return Info */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Return Information</h3>
            <div className="space-y-4">
              <div>
                <Label>Return Date *</Label>
                <Input
                  type="date"
                  value={header.return_date}
                  onChange={(e) => setHeader({...header, return_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Return Reason *</Label>
                <Select 
                  value={header.return_reason || '__SELECT__'} 
                  onValueChange={(v) => setHeader({...header, return_reason: v === '__SELECT__' ? '' : v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select Reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__SELECT__">Select Reason</SelectItem>
                    <SelectItem value="DAMAGED">Damaged in Transit</SelectItem>
                    <SelectItem value="DEFECTIVE">Defective Product</SelectItem>
                    <SelectItem value="WRONG_ITEM">Wrong Item Delivered</SelectItem>
                    <SelectItem value="NOT_AS_DESCRIBED">Not as Described</SelectItem>
                    <SelectItem value="CUSTOMER_CHANGED_MIND">Customer Changed Mind</SelectItem>
                    <SelectItem value="QUALITY_ISSUE">Quality Issue</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Return Note</Label>
                <textarea
                  value={header.return_note}
                  onChange={(e) => setHeader({...header, return_note: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                  rows={3}
                  placeholder="Additional notes..."
                  maxLength={500}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Items */}
        {selectedSO && items.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" /> Select Items to Return
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-center w-16">Select</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Material</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Delivered</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Return Qty</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item Return Reason</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item, index) => (
                    <tr
                      key={`ret-${item.delivery_number || 'd'}-${item.delivery_item || index}`}
                      className={item.selected ? 'bg-orange-50' : 'hover:bg-gray-50'}
                    >
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={item.selected}
                          onChange={(e) => handleItemToggle(index, e.target.checked)}
                          className="w-4 h-4 text-orange-600 rounded"
                        />
                      </td>
                      <td className="px-4 py-3 font-mono">{item.item_number.toString().padStart(4, '0')}</td>
                      <td className="px-4 py-3 font-mono text-sm">{item.material_id || '-'}</td>
                      <td className="px-4 py-3 text-sm">{item.item_description}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        {item.original_quantity} {item.unit_of_measure}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Input
                          type="number"
                          min="0"
                          max={item.original_quantity}
                          step="0.001"
                          value={item.return_quantity}
                          onChange={(e) => handleItemChange(index, 'return_quantity', e.target.value)}
                          className="w-24 text-right"
                          disabled={!item.selected}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Select 
                          value={item.return_reason || '__SELECT__'} 
                          onValueChange={(v) => handleItemChange(index, 'return_reason', v === '__SELECT__' ? '' : v)}
                          disabled={!item.selected}
                        >
                          <SelectTrigger className="w-40"><SelectValue placeholder="Reason" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__SELECT__">Select</SelectItem>
                            <SelectItem value="DAMAGED">Damaged</SelectItem>
                            <SelectItem value="DEFECTIVE">Defective</SelectItem>
                            <SelectItem value="WRONG_ITEM">Wrong Item</SelectItem>
                            <SelectItem value="QUALITY">Quality Issue</SelectItem>
                            <SelectItem value="OTHER">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        ${(item.net_price * (item.return_quantity || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td colSpan={7} className="px-4 py-3 text-right">Total Return Value:</td>
                    <td className="px-4 py-3 text-right font-mono text-lg">
                      ${items
                        .filter(i => i.selected && i.return_quantity > 0)
                        .reduce((sum, i) => sum + (i.net_price * parseFloat(i.return_quantity || 0)), 0)
                        .toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ReturnsOrderCreate;
